import React, { useState, useRef, useEffect, useMemo } from 'react'
import { X, Shield, Camera, CheckCircle, AlertCircle, Upload, RefreshCw, ArrowRight, ChevronLeft, ShieldCheck } from 'lucide-react'
import { Button } from '../ui/Button'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useAuth } from '../../contexts/AuthContext'
import { activityLog } from '../../services/activityLog'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import toast from 'react-hot-toast'

interface KYCVerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

export const KYCVerificationModal: React.FC<KYCVerificationModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const { verifyIdentity, refreshUser, user } = useAuth()
  useBodyScrollLock(isOpen)
  const [step, setStep] = useState<'document' | 'selfie' | 'confirm' | 'complete'>('document')
  const [documentType, setDocumentType] = useState<'drivers_license' | 'passport' | 'state_id'>('drivers_license')
  const [documentFront, setDocumentFront] = useState<File | null>(null)
  const [documentBack, setDocumentBack] = useState<File | null>(null)
  const [selfie, setSelfie] = useState<File | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const fileInputFrontRef = useRef<HTMLInputElement>(null)
  const fileInputBackRef = useRef<HTMLInputElement>(null)
  const fileInputSelfieRef = useRef<HTMLInputElement>(null)
  const [showCamera, setShowCamera] = useState(false)
  const [cameraMode, setCameraMode] = useState<'selfie' | 'document-front' | 'document-back' | null>(null)
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
  const [selectedCamera, setSelectedCamera] = useState<string>('')
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [videoReady, setVideoReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const documentFrontUrl = useMemo(
    () => documentFront ? URL.createObjectURL(documentFront) : null,
    [documentFront]
  )
  useEffect(() => {
    return () => {
      if (documentFrontUrl) URL.revokeObjectURL(documentFrontUrl)
    }
  }, [documentFrontUrl])

  const documentBackUrl = useMemo(
    () => documentBack ? URL.createObjectURL(documentBack) : null,
    [documentBack]
  )
  useEffect(() => {
    return () => {
      if (documentBackUrl) URL.revokeObjectURL(documentBackUrl)
    }
  }, [documentBackUrl])

  const selfieUrl = useMemo(
    () => selfie ? URL.createObjectURL(selfie) : null,
    [selfie]
  )
  useEffect(() => {
    return () => {
      if (selfieUrl) URL.revokeObjectURL(selfieUrl)
    }
  }, [selfieUrl])

  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [])

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      const video = videoRef.current
      video.srcObject = stream
      video.onloadedmetadata = () => {
        setVideoReady(true)
        video.play().catch(() => {})
      }
    }
  }, [showCamera, stream])

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setShowCamera(false)
    setVideoReady(false)
    setCameraMode(null)
  }

  const startCamera = async (mode: 'selfie' | 'document-front' | 'document-back') => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Camera not supported in this browser. Please upload a photo instead.')
        return
      }
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput')
      setCameras(videoDevices)
      if (videoDevices.length === 0) {
        toast.error('No camera detected. Please upload a photo instead.')
        return
      }
      const cameraId = selectedCamera || videoDevices[0].deviceId
      setSelectedCamera(cameraId)
      const facingMode = mode === 'selfie' ? 'user' : 'environment'
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: cameraId ? { exact: cameraId } : undefined,
          facingMode: cameraId ? undefined : facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
      setCameraMode(mode)
      setStream(mediaStream)
      setShowCamera(true)
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera permission denied. Please allow camera access or upload a photo instead.')
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No camera found. Please upload a photo instead.')
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Camera is already in use. Please close other apps using the camera.')
      } else {
        toast.error('Unable to access camera. Please upload a photo instead.')
      }
    }
  }

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current && cameraMode) {
      const video = videoRef.current
      const canvas = canvasRef.current
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (ctx) {
        if (cameraMode === 'selfie') {
          ctx.translate(canvas.width, 0)
          ctx.scale(-1, 1)
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = `${cameraMode}-${Date.now()}.jpg`
            const file = new File([blob], fileName, { type: 'image/jpeg' })
            if (cameraMode === 'selfie') {
              setSelfie(file)
            } else if (cameraMode === 'document-front') {
              setDocumentFront(file)
            } else if (cameraMode === 'document-back') {
              setDocumentBack(file)
            }
            stopCamera()
          }
        }, 'image/jpeg', 0.95)
      }
    }
  }

  const switchCamera = async () => {
    const currentIndex = cameras.findIndex(c => c.deviceId === selectedCamera)
    const nextIndex = (currentIndex + 1) % cameras.length
    const nextCamera = cameras[nextIndex].deviceId
    stopCamera()
    setSelectedCamera(nextCamera)
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: { exact: nextCamera },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
      setStream(mediaStream)
      setShowCamera(true)
    } catch (error: any) {
      toast.error('Unable to switch camera. Please try again.')
      setShowCamera(false)
    }
  }

  const handleFileSelect = (file: File, type: 'front' | 'back' | 'selfie') => {
    if (type === 'front') {
      setDocumentFront(file)
    } else if (type === 'back') {
      setDocumentBack(file)
    } else if (type === 'selfie') {
      setSelfie(file)
    }
  }

  const handleStartVerification = () => {
    if (!documentFront || !selfie) {
      toast.error('Please upload all required documents')
      return
    }
    if ((documentType === 'drivers_license' || documentType === 'state_id') && !documentBack) {
      toast.error('Please upload the back of your ID')
      return
    }

    setIsVerifying(true)
    verifyIdentity({})
      .then(() => refreshUser())
      .then(() => {
        toast.success('Identity verified successfully!')
        const userName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : undefined
        activityLog.logIdentityVerification(userName)
        setTimeout(() => {
          onComplete()
          handleReset()
        }, 1500)
      })
      .catch((error) => {
        toast.error(error?.message || 'Verification failed. Please try again.')
      })
      .finally(() => {
        setIsVerifying(false)
      })
  }

  const handleReset = () => {
    setStep('document')
    setDocumentFront(null)
    setDocumentBack(null)
    setSelfie(null)
    stopCamera()
  }

  const handleClose = () => {
    if (!isVerifying) {
      handleReset()
      onClose()
    }
  }

  const handleStepClick = (targetStep: 'document' | 'selfie' | 'confirm' | 'complete') => {
    if (isVerifying || targetStep === 'complete' || targetStep === 'confirm') {
      return
    }
    setStep(targetStep)
  }

  if (!isOpen) return null

  const stepOrder = ['document', 'selfie', 'confirm']
  const stepLabels: Record<string, string> = {
    document: 'ID',
    selfie: 'Selfie',
    confirm: 'Confirm',
  }

  const requiresBack = documentType === 'drivers_license' || documentType === 'state_id'

  return (
    <div className="modal-overlay modal-overlay-nested">
      <div className="bg-card rounded-xl shadow-2xl border border-border p-6 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                KYC/AML Verification
              </h2>
              <p className="text-sm text-muted-foreground">
                Required to start your 1031 exchange
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isVerifying}
            className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
          {stepOrder.map((s, idx) => {
            const stepKey = s as typeof step
            const isActive = step === stepKey
            const currentStepIndex = stepOrder.indexOf(step)
            const thisStepIndex = stepOrder.indexOf(stepKey)
            const completed = thisStepIndex < currentStepIndex
            const clickable = stepKey !== 'confirm' && !isVerifying

            return (
              <div key={s} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => handleStepClick(stepKey)}
                    disabled={!clickable}
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                      completed
                        ? 'bg-green-500 border-green-500 text-white'
                        : isActive
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'bg-muted border-border text-muted-foreground'
                    } ${
                      clickable ? 'cursor-pointer hover:border-primary hover:bg-primary/10' : 'cursor-default'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {completed ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <span className="text-xs font-semibold">{idx + 1}</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleStepClick(stepKey)}
                    disabled={!clickable}
                    className={`text-xs mt-1 text-center transition-colors whitespace-nowrap ${
                      isActive ? 'text-primary font-semibold' : 'text-muted-foreground'
                    } ${
                      clickable ? 'cursor-pointer hover:text-primary' : 'cursor-default'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {stepLabels[s]}
                  </button>
                </div>
                {idx < stepOrder.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    completed ? 'bg-green-500' : 'bg-border'
                  }`} />
                )}
              </div>
            )
          })}
        </div>

        {step === 'document' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">
                Document Type
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['drivers_license', 'passport', 'state_id'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setDocumentType(type)}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      documentType === type
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-border/80 text-foreground/80'
                    }`}
                  >
                    {type === 'drivers_license' ? 'Driver\'s License' : type === 'passport' ? 'Passport' : 'State ID'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Upload Government ID
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Please upload clear photos or PDFs (PNG, JPG, PDF) of your {documentType === 'drivers_license' ? 'driver\'s license' : documentType === 'passport' ? 'passport' : 'state ID'}
              </p>
            </div>

            <div className="space-y-4">
              {!showCamera || cameraMode !== 'document-front' ? (
                <div>
                  <label htmlFor="document-front" className="block text-sm font-medium text-foreground/80 mb-2">
                    Front of ID {documentFront && <CheckCircle className="inline h-4 w-4 text-green-500 ml-1" />}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => startCamera('document-front')}
                      className="p-6 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors flex flex-col items-center justify-center space-y-2"
                    >
                      <Camera className="h-8 w-8 text-muted-foreground/60" />
                      <span className="text-sm text-muted-foreground">Use Camera</span>
                    </button>
                    <input
                      id="document-front"
                      ref={fileInputFrontRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,application/pdf"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileSelect(file, 'front')
                      }}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputFrontRef.current?.click()}
                      className="p-6 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors flex flex-col items-center justify-center space-y-2"
                    >
                      <Upload className="h-8 w-8 text-muted-foreground/60" />
                      <span className="text-sm text-muted-foreground">Upload Photo</span>
                    </button>
                  </div>
                  {documentFrontUrl && (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                        <CheckCircle className="h-3 w-3" />
                        <span>Front captured successfully</span>
                      </div>
                      <div className={`relative mx-auto rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-700 ${
                        documentType === 'passport' ? 'w-full max-w-[240px] aspect-[2/3]' : 'w-full max-h-32 aspect-[1.6/1]'
                      }`}>
                        <img
                          src={documentFrontUrl}
                          alt="Document front preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={() => setDocumentFront(null)}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center space-x-1"
                      >
                        <RefreshCw className="h-3 w-3" />
                        <span>Replace</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Camera View - Front of ID
                  </label>
                  <div className="relative bg-black rounded-lg overflow-hidden min-h-[320px] flex items-center justify-center">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full min-h-[320px] object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                    />
                    {!videoReady && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <LoadingSpinner size="lg" variant="icon" className="text-white" />
                        <span className="ml-2 text-white text-sm">Initializing camera...</span>
                      </div>
                    )}
                    <canvas ref={canvasRef} className="hidden" />
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-3">
                    {cameras.length > 1 && (
                      <button
                        onClick={switchCamera}
                        className="h-10 w-10 flex items-center justify-center bg-card border border-border rounded-full shadow-sm hover:shadow-md transition-all"
                      >
                        <RefreshCw className="h-5 w-5 text-foreground/80" />
                      </button>
                    )}
                    <button
                      onClick={capturePhoto}
                      className="h-12 w-12 flex items-center justify-center bg-gradient-to-r from-primary to-primary/80 hover:from-primary/80 hover:to-primary rounded-full shadow-lg transition-all hover:scale-105"
                    >
                      <Camera className="h-6 w-6 text-white" />
                    </button>
                    <button
                      onClick={stopCamera}
                      className="h-10 w-10 flex items-center justify-center bg-card border border-border rounded-full shadow-sm hover:shadow-md transition-all"
                    >
                      <X className="h-5 w-5 text-foreground/80" />
                    </button>
                  </div>
                </div>
              )}

              {requiresBack && (
                !showCamera || cameraMode !== 'document-back' ? (
                  <div>
                    <label htmlFor="document-back" className="block text-sm font-medium text-foreground/80 mb-2">
                      Back of ID <span className="text-red-500">*</span> {documentBack && <CheckCircle className="inline h-4 w-4 text-green-500 ml-1" />}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => startCamera('document-back')}
                        className="p-6 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors flex flex-col items-center justify-center space-y-2"
                      >
                        <Camera className="h-8 w-8 text-muted-foreground/60" />
                        <span className="text-sm text-muted-foreground">Use Camera</span>
                      </button>
                      <input
                        id="document-back"
                        ref={fileInputBackRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,application/pdf"
                        capture="environment"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleFileSelect(file, 'back')
                        }}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputBackRef.current?.click()}
                        className="p-6 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors flex flex-col items-center justify-center space-y-2"
                      >
                        <Upload className="h-8 w-8 text-muted-foreground/60" />
                        <span className="text-sm text-muted-foreground">Upload Photo</span>
                      </button>
                    </div>
                    {documentBackUrl && (
                      <div className="mt-2 space-y-1">
                        <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                          <CheckCircle className="h-3 w-3" />
                          <span>Back captured successfully</span>
                        </div>
                        <div className="relative mx-auto rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-700 w-full max-h-32 aspect-[1.6/1]">
                          <img
                            src={documentBackUrl}
                            alt="Document back preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          onClick={() => setDocumentBack(null)}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center space-x-1"
                        >
                          <RefreshCw className="h-3 w-3" />
                          <span>Replace</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Camera View - Back of ID
                    </label>
                    <div className="relative bg-black rounded-lg overflow-hidden min-h-[320px] flex items-center justify-center">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`w-full min-h-[320px] object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                      />
                      {!videoReady && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <LoadingSpinner size="lg" variant="icon" className="text-white" />
                          <span className="ml-2 text-white text-sm">Initializing camera...</span>
                        </div>
                      )}
                      <canvas ref={canvasRef} className="hidden" />
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-3">
                      {cameras.length > 1 && (
                        <button
                          onClick={switchCamera}
                          className="h-10 w-10 flex items-center justify-center bg-card border border-border rounded-full shadow-sm hover:shadow-md transition-all"
                        >
                          <RefreshCw className="h-5 w-5 text-foreground/80" />
                        </button>
                      )}
                      <button
                        onClick={capturePhoto}
                        className="h-12 w-12 flex items-center justify-center bg-gradient-to-r from-primary to-primary/80 hover:from-primary/80 hover:to-primary rounded-full shadow-lg transition-all hover:scale-105"
                      >
                        <Camera className="h-6 w-6 text-white" />
                      </button>
                      <button
                        onClick={stopCamera}
                        className="h-10 w-10 flex items-center justify-center bg-card border border-border rounded-full shadow-sm hover:shadow-md transition-all"
                      >
                        <X className="h-5 w-5 text-foreground/80" />
                      </button>
                    </div>
                  </div>
                )
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setStep('selfie')}
                  disabled={!documentFront}
                  className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/80 hover:to-primary text-primary-foreground"
                >
                  Continue to Selfie
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'selfie' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Take a Selfie
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Please take a clear selfie or upload a photo (PNG, JPG) to verify your identity matches your ID
              </p>
            </div>

            {!showCamera || cameraMode !== 'selfie' ? (
              <div>
                <label htmlFor="selfie-photo" className="block text-sm font-medium text-foreground/80 mb-2">
                  Selfie {selfie && <CheckCircle className="inline h-4 w-4 text-green-500 ml-1" />}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => startCamera('selfie')}
                    className="p-6 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors flex flex-col items-center justify-center space-y-2"
                  >
                    <Camera className="h-8 w-8 text-muted-foreground/60" />
                    <span className="text-sm text-muted-foreground">Use Camera</span>
                  </button>
                  <input
                    id="selfie-photo"
                    ref={fileInputSelfieRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    capture="user"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileSelect(file, 'selfie')
                    }}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputSelfieRef.current?.click()}
                    className="p-6 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors flex flex-col items-center justify-center space-y-2"
                  >
                    <Upload className="h-8 w-8 text-muted-foreground/60" />
                    <span className="text-sm text-muted-foreground">Upload Photo</span>
                  </button>
                </div>
                {selfieUrl && (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                      <CheckCircle className="h-3 w-3" />
                      <span>Selfie captured successfully</span>
                    </div>
                    <div className="relative mx-auto rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-700 w-48 h-48 aspect-square">
                      <img
                        src={selfieUrl}
                        alt="Selfie preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => setSelfie(null)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center space-x-1"
                    >
                      <RefreshCw className="h-3 w-3" />
                      <span>Retake</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-2">
                  Camera View - Selfie
                </label>
                <div className="relative bg-black rounded-lg overflow-hidden min-h-[400px] flex items-center justify-center">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`w-full min-h-[400px] object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  {!videoReady && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <LoadingSpinner size="lg" variant="icon" className="text-white" />
                      <span className="ml-2 text-white text-sm">Initializing camera...</span>
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>
                <div className="mt-3 flex items-center justify-center gap-3">
                  {cameras.length > 1 && (
                    <button
                      onClick={switchCamera}
                      className="h-10 w-10 flex items-center justify-center bg-card border border-border rounded-full shadow-sm hover:shadow-md transition-all"
                    >
                      <RefreshCw className="h-5 w-5 text-foreground/80" />
                    </button>
                  )}
                  <button
                    onClick={capturePhoto}
                    className="h-12 w-12 flex items-center justify-center bg-gradient-to-r from-primary to-primary/80 hover:from-primary/80 hover:to-primary rounded-full shadow-lg transition-all hover:scale-105 ring-2 ring-primary-foreground/40"
                  >
                    <Camera className="h-6 w-6 text-white" />
                  </button>
                  <button
                    onClick={stopCamera}
                    className="h-10 w-10 flex items-center justify-center bg-card border border-border rounded-full shadow-sm hover:shadow-md transition-all"
                  >
                    <X className="h-5 w-5 text-foreground/80" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep('document')}
                className="flex-1"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={() => setStep('confirm')}
                disabled={!selfie}
                className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/80 hover:to-primary text-primary-foreground"
              >
                Continue to Confirm
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-2">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">
                Review and Confirm
              </h3>
              <p className="text-xs text-muted-foreground mb-2">
                Please review your information before submitting
              </p>
            </div>

            <div className="flex gap-3 flex-wrap">
              {documentFrontUrl && (
                <div className="flex flex-col items-center gap-1">
                  <div className="h-24 w-24 rounded-lg overflow-hidden border border-border">
                    <img
                      src={documentFrontUrl}
                      alt="ID Front thumbnail"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">ID Front</span>
                </div>
              )}
              {documentBackUrl && requiresBack && (
                <div className="flex flex-col items-center gap-1">
                  <div className="h-24 w-24 rounded-lg overflow-hidden border border-border">
                    <img
                      src={documentBackUrl}
                      alt="ID Back thumbnail"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">ID Back</span>
                </div>
              )}
              {selfieUrl && (
                <div className="flex flex-col items-center gap-1">
                  <div className="h-24 w-24 rounded-lg overflow-hidden border border-border">
                    <img
                      src={selfieUrl}
                      alt="Selfie thumbnail"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">Selfie</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {documentFront && (
                <div className="flex items-center space-x-2 p-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                  <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-foreground/80">
                    {documentType === 'passport' ? 'Passport' : documentType === 'drivers_license' ? 'Driver\'s License' : 'State ID'} — uploaded
                  </span>
                </div>
              )}

              {selfie && (
                <div className="flex items-center space-x-2 p-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                  <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-foreground/80">Selfie — uploaded</span>
                </div>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  By clicking "Submit", you confirm that all information is accurate and the documents belong to you.
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep('selfie')}
                disabled={isVerifying}
                className="flex-1"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={handleStartVerification}
                disabled={isVerifying}
                className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/80 hover:to-primary text-primary-foreground"
              >
                {isVerifying ? (
                  <>
                    <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Submit Verification
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
